import React, { ReactElement, useEffect, useRef, useState } from 'react';

import { FooterTaxonomy } from 'types';
import { envData } from '@src/environments';
import { footerTaxoSelector } from 'store/selectors/taxonomySelector';
import { storeIdentifierNameSelector } from 'store/selectors/commonSelector';
import { useAppSelector } from 'store/store';
import useEmailSignUp from 'hooks/useEmailSignUp';
import { aosStoreIdentifierNames } from 'utility/utility';
import ScoreMore from '../ScoreMore/ScoreMore';
import ConnectWithUs from '../ConnectWithUs/ConnectWithUs';
import './MiddleSection.scss';

const MiddleSection = () => {
  const [signUpErrorMessage, setSignUpErrorMessage] = useState(
    'Please enter an email in the format username@domain.com',
  );
  const [displaySignUpSuccess, setDisplaySignUpSuccess] = useState(false);
  const [signUpSuccessMessage, setSignUpSuccessMessage] = useState<ReactElement | string>('');
  const [inputFocused, setInputFocused] = useState(false);
  const emailSignupInput = useRef<HTMLInputElement>();
  const taxonomy = useAppSelector(footerTaxoSelector);

  const communications: FooterTaxonomy['Communication Section'] = taxonomy['Communication Section'];
  const score: FooterTaxonomy['Score Section'] = taxonomy?.['Score Section']?.[0] || {};
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const { chatUrl, socialLinks } = envData[storeIdentifierName];
  const [emailSignUpResponse, emailSignUpError, updateEmailSignUp, signUpAnalytics, validateEmail] =
    useEmailSignUp();

  useEffect(() => {
    if (emailSignUpResponse && emailSignUpResponse.status === 200) {
      setDisplaySignUpSuccess(true);
      setSignUpErrorMessage('');
      setSignUpSuccessMessage(
        <p className="hmf-body-s">
          <span className="dsg-react-footer-sign-up-success-msg">Welcome back!</span> You&apos;ve
          already received this offer. You have been resubscribed to receive other offers.
        </p>,
      );
      signUpAnalytics(
        `Welcome back! You've already received this offer. You have been resubscribed to receive other offers.`,
        emailSignupInput.current.value,
      );
    } else if (emailSignUpResponse && emailSignUpResponse.status === 201) {
      setDisplaySignUpSuccess(true);
      setSignUpErrorMessage('');
      setSignUpSuccessMessage(
        <p className="hmf-body-s">
          <span className="dsg-react-footer-sign-up-success-msg">Success!</span> Your discount is on
          its way.
        </p>,
      );
      signUpAnalytics(`Your discount is on its way.`, emailSignupInput.current.value);
    } else if (emailSignupInput && emailSignupInput.current && emailSignupInput.current.value) {
      setDisplaySignUpSuccess(false);
      setSignUpErrorMessage(
        emailSignUpResponse && emailSignUpResponse.data
          ? emailSignUpResponse.data.user_message
          : null,
      );
      setSignUpSuccessMessage('');
      setInputFocused(true);
      signUpAnalytics(
        emailSignUpResponse && emailSignUpResponse.data
          ? emailSignUpResponse.data.user_message
          : null,
        emailSignupInput.current.value,
      );
    }
  }, [emailSignUpResponse]);

  const inputFocusedToggle = (event) => {
    setInputFocused(!inputFocused);
  };

  const doEmailValidation = () => {
    const email = emailSignupInput.current.value;
    inputFocusedToggle();
    if (email) {
      const isEmailValid = validateEmail(email);
      if (isEmailValid) {
        return true;
      }
      setSignUpErrorMessage('Please enter an email in the format username@domain.com');
      return false;
    }
    return false;
  };

  const signUpFromFooter = (event) => {
    event?.preventDefault();
    const isEmailValid = validateEmail(emailSignupInput.current?.value);
    if (isEmailValid) {
      updateEmailSignUp(emailSignupInput.current?.value);
    }
  };

  return aosStoreIdentifierNames.includes(storeIdentifierName) ? null : (
    <div className="footer-middle-section">
      <div className="grid hmf-my-0 hmf-mx-auto">
        <ConnectWithUs
          communications={communications}
          emailOnBlur={doEmailValidation}
          emailOnSignUp={signUpFromFooter}
          emailOnFocus={inputFocusedToggle}
          emailFocusState={inputFocused}
          emailErrorStatus={emailSignUpError}
          emailSuccessStatus={displaySignUpSuccess}
          emailErrorMessage={signUpErrorMessage}
          emailSuccessMessage={signUpSuccessMessage}
          chatUrl={chatUrl}
          social={socialLinks}
          storeIdentifier={storeIdentifierName}
          emailSignupInput={emailSignupInput}
        />
        <ScoreMore score={score} />
      </div>
    </div>
  );
};

export default MiddleSection;
